import { CheckCircle2 } from "lucide-react";
import { useId, useRef, useState } from "react";
import { toast } from "sonner";
import { flatten, safeParse } from "valibot";
import { type ContactSubmitSource, track } from "~/lib/analytics";
import { contactSchema } from "~/lib/validators";
import * as m from "~/paraglide/messages.js";

type FieldName = "email" | "message";
type FieldErrors = Partial<Record<FieldName, string>>;

/**
 * The page's highest-stakes moment, so it is drawn in the site's own vocabulary
 * rather than the shared shadcn primitives: the design's field (7px corner,
 * hairline ring, 8px/12px padding) and its `.button-solid`, styled by
 * `.contact-form` in `styles/lindevo/page-contact.css`.
 *
 * Validation is `contactSchema` run directly through valibot. A two-field form
 * does not need a form library on top of the schema it already has, and owning
 * the submit path is what makes the focus move and the error tone possible.
 */
export const ContactForm = ({
  onMessageSent,
  defaultMessage = "",
  source,
}: {
  defaultMessage?: string;
  onMessageSent?: () => void;
  source: ContactSubmitSource;
}) => {
  const fieldId = useId();
  const [values, setValues] = useState({
    email: "",
    message: defaultMessage,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  // Left empty by people and filled by bots. Never shown, never required.
  const trapRef = useRef<HTMLInputElement>(null);

  const setField = (name: FieldName, value: string) => {
    setValues((current) => ({ ...current, [name]: value }));
    // Clear the field's own error as soon as it is edited; re-validating every
    // keystroke would mark an address invalid while it is still being typed.
    setErrors((current) =>
      current[name] ? { ...current, [name]: undefined } : current
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const result = safeParse(contactSchema, values);
    if (!result.success) {
      const flat = flatten<typeof contactSchema>(result.issues).nested ?? {};
      const next: FieldErrors = {
        email: flat.email?.[0],
        message: flat.message?.[0],
      };
      setErrors(next);
      // Focus follows the problem. Leaving focus on the submit button makes a
      // screen-reader user hunt for what went wrong.
      const target = next.email ? emailRef.current : messageRef.current;
      target?.focus();
      return;
    }

    // A filled trap is a bot. Report success and send nothing, so the bot
    // learns neither that it was caught nor which field caught it.
    if (trapRef.current?.value) {
      setSentTo(values.email);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/contact", {
        body: JSON.stringify(result.output),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      toast.success(m.contact_form_success());
      setSentTo(result.output.email);
      setValues({ email: "", message: "" });
      onMessageSent?.();
      track("contact_submit", { source });
    } catch {
      toast.error(m.contact_form_error());
    } finally {
      setIsLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div className="form-success" role="status">
        <span className="form-success-mark">
          <CheckCircle2 aria-hidden="true" size={22} />
        </span>
        <h3>{m.contact_form_success_title()}</h3>
        <p>{m.contact_form_success_body({ email: sentTo })}</p>
        <button
          className="button button-outline"
          onClick={() => setSentTo(null)}
          type="button"
        >
          {m.contact_form_success_reset()}
        </button>
      </div>
    );
  }

  const emailId = `${fieldId}-email`;
  const messageId = `${fieldId}-message`;

  return (
    // `noValidate`: the browser's own bubbles are unstyled, English-only and
    // land before the schema runs, so the form owns its messages.
    <form className="contact-form" noValidate onSubmit={handleSubmit}>
      <div className="field">
        <label htmlFor={emailId}>{m.contact_form_email_label()}</label>
        <input
          aria-describedby={errors.email ? `${emailId}-error` : undefined}
          aria-invalid={Boolean(errors.email)}
          autoComplete="email"
          id={emailId}
          name="email"
          onChange={(e) => setField("email", e.target.value)}
          placeholder={m.contact_form_email_placeholder()}
          ref={emailRef}
          required
          type="email"
          value={values.email}
        />
        {errors.email ? (
          <p className="field-error" id={`${emailId}-error`}>
            {errors.email}
          </p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor={messageId}>{m.contact_form_message_label()}</label>
        <textarea
          aria-describedby={
            errors.message ? `${messageId}-error` : `${messageId}-hint`
          }
          aria-invalid={Boolean(errors.message)}
          id={messageId}
          name="message"
          onChange={(e) => setField("message", e.target.value)}
          placeholder={m.contact_form_message_placeholder()}
          ref={messageRef}
          required
          rows={5}
          value={values.message}
        />
        {errors.message ? (
          <p className="field-error" id={`${messageId}-error`}>
            {errors.message}
          </p>
        ) : (
          <p className="field-hint" id={`${messageId}-hint`}>
            {m.contact_form_message_hint()}
          </p>
        )}
      </div>

      {/* Hidden from people and from assistive technology; only a script that
          fills every input reaches it. */}
      <div aria-hidden="true" className="field-trap">
        <label htmlFor={`${fieldId}-company`}>
          {m.contact_form_trap_label()}
        </label>
        <input
          autoComplete="off"
          defaultValue=""
          id={`${fieldId}-company`}
          name="company"
          ref={trapRef}
          tabIndex={-1}
          type="text"
        />
      </div>

      <button
        className="button button-solid"
        disabled={isLoading}
        type="submit"
      >
        {isLoading ? m.contact_form_sending() : m.contact_form_submit()}
      </button>
    </form>
  );
};
